import { sumDiscountsCents } from '../../quote-builder/marginEngine';

/**
 * computeSigningTotals: the customer-facing total on the e-sign page (IDI-370).
 *
 * MUST produce the same number as the server's pricing.DeriveAcceptedTotal
 * (backend-go/internal/pricing/derive.go), which it is checked against via
 * client_total_cents. Both mirror the builder's computeQuote:
 *
 *   subtotal  = selected line totals (computeSelectionTotal)
 *   discount  = sumDiscountsCents(quote.discounts, subtotal)   (clamped)
 *   tax       = round((subtotal - discount) * rate)            (0 if exempt)
 *   total     = subtotal - discount + tax
 *
 * Before this the page showed and sent the pre-tax, pre-discount subtotal,
 * so every taxed quote was refused (409 total_mismatch) and the customer saw
 * the wrong total (Q-0095: page $6,415.38 vs quote $6,736.15).
 */
export default function computeSigningTotals(subtotalCents, document) {
  const subtotal = Math.max(0, Math.round(Number(subtotalCents) || 0));
  const discounts = Array.isArray(document?.discounts) ? document.discounts : [];
  const discount = sumDiscountsCents(discounts, subtotal);
  const exempt = document?.tax_exempt === true;
  const rawRate = Number(document?.tax_rate ?? document?.taxRate ?? 0);
  // Stored as a decimal (0.06), used as-is exactly like the server. No
  // "percent" rescue here: silently reinterpreting 6 as 6% would make the page
  // disagree with the server's check (0 stored quotes have a rate > 1).
  const rate = exempt || !Number.isFinite(rawRate) || rawRate <= 0 ? 0 : rawRate;
  const taxable = subtotal - discount;
  // Math.floor(x + 0.5) === Go's roundHalfUp for these non-negative amounts.
  const tax = rate > 0 ? Math.floor(taxable * rate + 0.5) : 0;
  return { subtotal, discount, taxRate: rate, tax, total: taxable + tax };
}
